import type { ProviderLevel } from '@ems/clinical';
import { BookOpen, ChevronRight, Library, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Badge, ButtonLink, EmptyState, Input, PageHeader, QueryError, Segmented, Spinner } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { cn, relativeTime } from '@/lib/utils';
import { usePrefs } from '@/stores/prefs';
import type { Protocol } from '@/types/db';
import { useCategories, useEffectiveProtocols, usePendingAcknowledgments } from './api';

export function ProtocolScopeBadge({ protocol }: { protocol: Pick<Protocol, 'org_id' | 'state_code' | 'is_sample' | 'parent_id'> }) {
  return (
    <>
      {protocol.org_id ? (
        <Badge tone="brand">{protocol.parent_id ? 'Agency (adapted)' : 'Agency'}</Badge>
      ) : protocol.state_code ? (
        <Badge tone="brand">{protocol.state_code} state</Badge>
      ) : (
        <Badge tone="neutral">National</Badge>
      )}
      {protocol.is_sample && <Badge tone="warning">Sample</Badge>}
    </>
  );
}

function isRecentlyUpdated(p: Protocol) {
  return p.version > 1 && p.published_at !== null && Date.now() - new Date(p.published_at).getTime() < 30 * 86400000;
}

export function ProtocolsPage() {
  const { activeMembership, profile } = useAuth();
  const { filterByLevel, setFilterByLevel } = usePrefs();
  const protocols = useEffectiveProtocols();
  const categories = useCategories();
  const pending = usePendingAcknowledgments();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [population, setPopulation] = useState<'all' | 'adult' | 'pediatric'>('all');

  const level = (activeMembership?.certification_level ?? profile?.certification_level) as ProviderLevel | null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (protocols.data ?? [])
      .filter((p) => category === 'all' || p.category_slug === category)
      .filter((p) => population === 'all' || p.population === 'all' || p.population === population || (population === 'pediatric' && p.population === 'neonate'))
      .filter((p) => !filterByLevel || !level || p.provider_levels.length === 0 || p.provider_levels.includes(level))
      .filter((p) => !q || [p.title, p.code ?? '', p.summary ?? '', ...p.tags].some((s) => s.toLowerCase().includes(q)))
      .sort((a, b) => (a.code ?? a.title).localeCompare(b.code ?? b.title, undefined, { numeric: true }));
  }, [protocols.data, query, category, population, filterByLevel, level]);

  const grouped = useMemo(() => {
    const byCat = new Map<string, Protocol[]>();
    for (const p of filtered) byCat.set(p.category_slug, [...(byCat.get(p.category_slug) ?? []), p]);
    return (categories.data ?? []).filter((c) => byCat.has(c.slug)).map((c) => ({ category: c, items: byCat.get(c.slug)! }));
  }, [filtered, categories.data]);

  const usedCategories = new Set((protocols.data ?? []).map((p) => p.category_slug));

  return (
    <div>
      <PageHeader
        title="Protocols"
        subtitle={activeMembership ? `${activeMembership.organization.name}${activeMembership.organization.state_code ? ` · ${activeMembership.organization.state_code}` : ''}` : 'National model library'}
        actions={
          <ButtonLink to="/library" variant="outline" size="sm" icon={<Library className="size-4" />}>
            State library
          </ButtonLink>
        }
      />

      {(pending.data?.length ?? 0) > 0 && (
        <Alert tone="warning" title={`${pending.data!.length} protocol update${pending.data!.length > 1 ? 's' : ''} require acknowledgment`} className="mb-4">
          <ul className="mt-1 space-y-0.5">
            {pending.data!.map((p) => (
              <li key={p.protocol_id}>
                <Link to={`/protocols/${p.protocol_id}`} className="font-medium underline">
                  {p.title} (v{p.version})
                </Link>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {!activeMembership && (
        <Alert tone="info" className="mb-4" title="Showing the national model library" action={<ButtonLink size="sm" to="/profile">Join an agency</ButtonLink>}>
          Join or create an organization to see your agency’s protocols and state library.
        </Alert>
      )}

      <div className="mb-3 space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter protocols" className="h-12 pl-10 text-base" aria-label="Filter protocols" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            size="sm"
            value={population}
            onChange={setPopulation}
            options={[
              { value: 'all', label: 'All ages' },
              { value: 'adult', label: 'Adult' },
              { value: 'pediatric', label: 'Pediatric' },
            ]}
          />
          {level && (
            <button
              type="button"
              aria-pressed={filterByLevel}
              onClick={() => setFilterByLevel(!filterByLevel)}
              className={cn('rounded-md border px-2.5 py-1 text-xs font-medium transition-colors', filterByLevel ? 'border-stone-900 bg-stone-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-900' : 'border-stone-300 bg-white text-stone-700 hover:border-stone-500 hover:text-stone-950 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-300 dark:hover:border-stone-500 dark:hover:text-white')}
            >
              My level only
            </button>
          )}
        </div>
        <div className="scrollbar-none -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6">
          {[{ slug: 'all', name: 'All' }, ...(categories.data ?? []).filter((c) => usedCategories.has(c.slug))].map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setCategory(c.slug)}
              className={cn(
                'whitespace-nowrap rounded-md border px-2.5 py-1 text-sm font-medium transition-colors',
                category === c.slug ? 'border-stone-900 bg-stone-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-900' : 'border-stone-300 bg-white text-stone-700 hover:border-stone-500 hover:text-stone-950 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-300 dark:hover:border-stone-500 dark:hover:text-white',
              )}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {protocols.isPending ? (
        <Spinner />
      ) : protocols.isError && !protocols.data ? (
        <QueryError error={protocols.error} retry={() => protocols.refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<BookOpen className="size-10" />} title="No protocols match">
          Try a different search or category.
        </EmptyState>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ category: cat, items }) => (
            <section key={cat.slug}>
              <h2 className="label-caps mb-2 text-stone-500 dark:text-stone-400">{cat.name}</h2>
              <ul className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
                {items.map((p) => (
                  <li key={p.id}>
                    <Link to={`/protocols/${p.id}`} className="flex items-center gap-3 px-1 py-3 transition-colors hover:bg-stone-100 dark:hover:bg-ink-900">
                      {p.code && <span className="w-12 shrink-0 font-mono text-xs font-medium text-stone-500">{p.code}</span>}
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium">{p.title}</span>
                          <ProtocolScopeBadge protocol={p} />
                          {isRecentlyUpdated(p) && <Badge tone="success">Updated</Badge>}
                        </span>
                        {p.summary && <span className="mt-0.5 line-clamp-1 block text-sm text-stone-500 dark:text-stone-400">{p.summary}</span>}
                      </span>
                      <ChevronRight className="size-4 shrink-0 text-stone-300 dark:text-stone-600" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {protocols.dataUpdatedAt > 0 && (
        <p className="mt-6 font-mono text-xs text-stone-500">
          {filtered.length} protocols · saved for offline use · synced {relativeTime(new Date(protocols.dataUpdatedAt))}
        </p>
      )}
    </div>
  );
}
